import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AdBanner } from '../components/layout/AdBanner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Trophy, Clock, Target, TrendUp, Warning, Question, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

export default function Dashboard() {
  const { user, API } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('mockrank_tour_seen')) {
      setShowTour(true);
    }
  }, []);

  const dismissTour = () => {
    localStorage.setItem('mockrank_tour_seen', 'true');
    setShowTour(false);
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/metrics`, { withCredentials: true });
        setMetrics(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchMetrics();
  }, [API]);

  if (!metrics) return <div className="p-8 text-center uppercase tracking-widest animate-pulse">Initializing Telemetry...</div>;

  return (
    <div className="container py-8 space-y-8 animate-in fade-in duration-500" data-testid="dashboard-page">
      
      {showTour && (
        <div className="bg-primary/10 border border-primary p-6 relative rounded-sm shadow-2xl animate-in slide-in-from-top-4">
          <button onClick={dismissTour} className="absolute top-4 right-4 text-primary hover:text-white transition-colors">
            <X size={24} />
          </button>
          <h2 className="font-heading text-2xl text-white uppercase tracking-widest flex items-center gap-2 mb-4">
            <Question size={28} className="text-primary"/> New Recruit Walkthrough
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground font-body">
            <div className="space-y-2">
              <p><strong className="text-white">1. Secure Your Gear:</strong> Head to <em>Settings</em> and enter your free Google Gemini API Key to activate the AI Engine.</p>
              <p><strong className="text-white">2. Enter Combat:</strong> Navigate to <em>Mock Tests</em> and generate a custom 10-question drill for your specific exam.</p>
            </div>
            <div className="space-y-2">
              <p><strong className="text-white">3. Track Standings:</strong> Check the <em>Leaderboard</em> to see how your speed and accuracy compare nationwide.</p>
              <p><strong className="text-white">4. Debrief:</strong> Visit the <em>AI Coach</em> to get an automated weakness analysis and personalized study strategy.</p>
            </div>
          </div>
          <Button onClick={dismissTour} className="mt-6 rounded-none font-bold tracking-widest uppercase bg-primary hover:bg-primary/80 text-white px-8">
            ACKNOWLEDGE & START
          </Button>
        </div>
      )}

      {!user?.ai_api_key && (
        <div className="bg-destructive/20 border border-destructive p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-destructive uppercase tracking-widest flex items-center gap-2"><Warning size={20}/> API KEY REQUIRED</h3>
            <p className="text-sm text-white mt-1">You must configure your personal Gemini API Key to unlock AI features (Mock Tests, Coach, Analysis). Getting a key is free.</p>
          </div>
          <Button onClick={() => window.location.href='/settings'} className="bg-destructive hover:bg-destructive/80 text-white rounded-none whitespace-nowrap">CONFIGURE NOW</Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start md:items-stretch">
        <div className="flex-1 space-y-2">
          <h1 className="text-4xl md:text-5xl font-heading text-white">COMMAND CENTER</h1>
          <p className="text-muted-foreground uppercase tracking-widest font-bold text-sm">Welcome back, {user?.name}</p>
        </div>
        <Card className="flex-1 bg-secondary border-primary/20 rounded-none shadow-none">
          <CardContent className="p-6">
            <div className="uppercase tracking-widest text-primary text-xs font-bold mb-2">DAILY INTEL</div>
            <blockquote className="text-lg font-body italic border-l-2 border-primary pl-4 mb-3 text-white/90">"{metrics.daily?.quote}"</blockquote>
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Lightning size={16} className="text-accent"/> {metrics.daily?.fact}</p>
          </CardContent>
        </Card>
      </div>

      <AdBanner />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard title="TESTS COMPLETED" value={metrics.total_tests} icon={<Trophy size={32} className="text-chart-1" />} />
        <MetricCard title="ACCURACY" value={`${metrics.accuracy_percent}%`} icon={<Target size={32} className="text-chart-2" />} />
        <MetricCard title="AVG TIME/QUESTION" value={`${metrics.time_per_question_sec}s`} icon={<Clock size={32} className="text-chart-3" />} />
      </div>

      <Card className="rounded-none border-border bg-card">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2"><TrendUp className="text-primary"/> PERFORMANCE TRAJECTORY</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px]">
          {metrics.trend.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" />
                <XAxis dataKey="date" stroke="#8E8E93" tickFormatter={(v) => new Date(v).toLocaleDateString()} />
                <YAxis stroke="#8E8E93" />
                <Tooltip contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #2C2C2E' }} />
                <Line type="monotone" dataKey="score" stroke="#007AFF" strokeWidth={3} dot={{ fill: '#007AFF' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground font-bold tracking-widest uppercase text-sm border border-dashed border-border">NO TELEMETRY DATA AVAILABLE</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, icon }) {
  return (
    <Card className="rounded-none border-border bg-card hover:border-primary/50 transition-colors">
      <CardContent className="p-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">{title}</p>
          <p className="text-4xl font-heading text-white">{value}</p>
        </div>
        <div className="p-3 bg-secondary border border-border">{icon}</div>
      </CardContent>
    </Card>
  );
}

function Lightning(props) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Zm-113,90.46,11.33-56.63a8,8,0,0,0-4.66-9.14L56.83,123l84.41-90.46-11.33,56.63a8,8,0,0,0,4.66,9.14l52.65,19.74Z"/></svg>;
}